/**
 * Cross-account session reset — the tenant-isolation seam.
 *
 * Zustand data stores are in-memory singletons and account changes happen
 * client-side (no page reload), so when the signed-in user *identity* changes
 * (login, logout, A→B account swap) every per-user data store must be wiped and
 * the React-Query cache cleared — otherwise the previous user's data
 * (contacts, campaigns, inbox, billing, sender emails, …) lingers and can be
 * shown to, or 404 the ownership guard for, the newly signed-in account.
 *
 * The guard keys on the user *id*, NOT the object reference: a token refresh
 * produces a new user object for the SAME user, and that must NOT nuke live
 * session state mid-use.
 *
 * Extracted from main.tsx's auth subscription so this (tenant-critical) logic
 * is unit-testable in isolation — main.tsx only wires it to
 * useAuthStore.subscribe.
 */

export interface ResettableStore {
  getState: () => { reset: () => void }
}

export interface SessionResetDeps {
  /** The per-user data stores to wipe on an account switch. */
  stores: ResettableStore[]
  /** Clears the React-Query server-state cache. */
  clearQueryCache: () => void
}

/** True when the signed-in identity changed (login / logout / A→B swap), as
 *  opposed to the same user getting a fresh object (token refresh). */
export function isAccountSwitch(
  current: { id?: string | number | null } | null | undefined,
  previous: { id?: string | number | null } | null | undefined,
): boolean {
  return (current?.id ?? null) !== (previous?.id ?? null)
}

/** Wipe per-user stores + query cache IFF the account identity changed.
 *  Returns true when a reset was performed (useful for assertions/telemetry). */
export function resetOnAccountSwitch(
  current: { id?: string | number | null } | null | undefined,
  previous: { id?: string | number | null } | null | undefined,
  deps: SessionResetDeps,
): boolean {
  if (!isAccountSwitch(current, previous)) return false
  for (const store of deps.stores) {
    store.getState().reset()
  }
  deps.clearQueryCache()
  return true
}
